import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/firestore";
import "firebase/compat/database";
import "firebase/compat/storage";

import { COLLECTION } from "../shared/strings/firebase";
import { setUserOfflineStatus, setUserOnlineStatus } from "services/users";

// Prefer VITE_FIREBASE_APP_ID if present; fallback to legacy VITE_FIREBASE_MESSAGING_APP_ID env name
const resolvedAppId =
  import.meta.env.VITE_FIREBASE_APP_ID ||
  import.meta.env.VITE_FIREBASE_MESSAGING_APP_ID;

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: resolvedAppId,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Log Firebase configuration status for debugging
// console.log("[Firebase] Configuration:", {
//   hasApiKey: !!config.apiKey,
//   hasAuthDomain: !!config.authDomain,
//   projectId: config.projectId || "NOT_SET",
//   hasAppId: !!config.appId,
// });

// Display warning if Firebase is not properly configured
if (!config.apiKey || config.apiKey === "undefined") {
  console.error(
    "[Firebase] Missing API key! Firebase authentication will not work."
  );
  console.error(
    "[Firebase] Please ensure .env file exists with VITE_FIREBASE_API_KEY set."
  );
}

if (!config.appId || config.appId === "undefined") {
  console.warn(
    "[Firebase] Missing App ID. Set VITE_FIREBASE_APP_ID (preferred) or VITE_FIREBASE_MESSAGING_APP_ID in your .env file."
  );
}

firebase.initializeApp(config);

export const firebaseInstance = firebase;
// Incase we want to use explicitly
export const firestore = firebase.firestore();

// Wrap auth initialization to handle invalid API key gracefully
let auth;
try {
  auth = firebase.auth();
  auth.useDeviceLanguage();
} catch (error) {
  console.error("[Firebase] Auth initialization failed:", error.message);
  // Create a mock auth object that won't crash the app
  auth = {
    onAuthStateChanged: (callback) => {
      console.warn("[Firebase] Mock auth: No user logged in");
      callback(null);
      return () => { }; // Return unsubscribe function
    },
    signInWithEmailAndPassword: () =>
      Promise.reject({ code: "auth/not-allowed" }),
    signInWithPopup: () => Promise.reject({ code: "auth/not-allowed" }),
    signOut: () => Promise.resolve(),
    fetchSignInMethodsForEmail: () => Promise.resolve([]),
    useDeviceLanguage: () => { },
  };
}

export { auth };
export const storage = firebase.storage();
//firestore.settings({ experimentalAutoDetectLongPolling: true });
// firestore.useEmulator("localhost", 9292);

// All firebase functions should be encapsulated within this class

const onVisibilityChangeCallback = (uid) => (ev) => {
  ev.preventDefault();
  ev.stopPropagation();
  if (window.document?.visibilityState === "visible") {
    setUserOnlineStatus(uid);
    return;
  }
  setUserOfflineStatus(uid);
};
export class Firebase {
  _onVisibilityChangeCallback;
  constructor() {
    this.firebase = firebase;
    this.firestore = firestore;
    this.auth = auth;
    // if (document.location.hostname === "localhost") {
    // 	this.firestore.settings({ host: "localhost:8080", ssl: false });
    // 	this.auth.useEmulator("http://localhost:9099");
    // }
  }

  // Auth State Change from firebase auth
  // @param callback: Function = function to call on auth change
  _onAuthStateChanged(callback = () => null) {
    return this.auth.onAuthStateChanged(callback);
  }

  // Find document with custom where
  // @param collection:String = Collection name
  // @param where:Any = Custom where clause
  _findDoc(collection, where) {
    return this.firestore
      .collection(collection)
      .where(...where)
      .get();
  }

  // Find document by document ID
  // @param collection:String = Collection name
  // @param docId:String = Document ID
  _findDocById(collection, docId) {
    return this.firestore.collection(collection).doc(docId).get();
  }

  // Find docs in Organisation with the given email
  // @param email: String = email to identify
  _findOrganisationByEmail(email) {
    return this._findDoc(COLLECTION.Organisations, ["email", "==", email]);
  }

  _findCityByEmail(email) {
    return this._findDoc(COLLECTION.Cities, ["email", "==", email]);
  }

  _findById(collection, uid) {
    return this._findDoc(collection, ["id", "==", uid]);
  }

  _updateLastSignInIfExternal(uid) {
    return this._findById(COLLECTION.externalUsers, uid).then(
      async (external) => {
        if (!external.empty) {
          external.docs[0].ref.update({
            lastSignIn: this.firebase.firestore.Timestamp.now(),
          });
        }
      }
    );
  }
  // SignOut current user
  _signOut() {
    return this.auth.signOut();
  }

  _resetPassword(email) {
    return this.auth.sendPasswordResetEmail(email);
  }

  _stopTrackingOnlineStatus(uid) {
    if (uid) {
      setUserOfflineStatus(uid);
    }

    if (this._onVisibilityChangeCallback) {
      window.document.removeEventListener(
        "visibilitychange",
        this._onVisibilityChangeCallback
      );
      window.document.removeEventListener(
        "beforeunload",
        this._onVisibilityChangeCallback
      );
    }

    this.firebase.database().ref(".info/connected").off("value");
  }

  _startTrackingOnlineStatus(uid) {
    const connectedRef = this.firebase.database().ref(".info/connected");

    this._onVisibilityChangeCallback = onVisibilityChangeCallback(uid);
    window.document.addEventListener(
      "visibilitychange",
      this._onVisibilityChangeCallback
    );
    window.document.addEventListener(
      "beforeunload",
      this._onVisibilityChangeCallback
    );

    connectedRef.on("value", async (snapshot) => {
      if (snapshot.val() === true) {
        setUserOnlineStatus(uid);
      } else {
        setUserOfflineStatus(uid);
      }
    });
  }
}

export default firebase;
